import {Injectable, Injector} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {Action, select, Store} from '@ngrx/store';
import {Observable} from 'rxjs/Observable';
import {catchError, filter, map, switchMap, take, withLatestFrom} from 'rxjs/operators';
import {
  BpmnProcessActionTypes,
  BpmnXmlActionTypes,
  FetchBpmnXmlAction,
  FetchExecutionHistoryAction,
  InitExecutionAction,
  LoadExsitingExecutionAction,
  RestartExecutionAction,
  StartExecutionAction,
  UpdateBpmnDeploymentIdentifierAction,
  UpdateBpmnXmlAction,
  UpdateExecutionHistoryParsingStatusAction,
  UpdateProcessExecutionStatus,
  UpdateProcessInstanceAction,
  UpdateUserSelectionStatusAction,
  UserClickedExecutedNodeAction
} from '../actions';
import {BpmnEngineService} from '../../services/bpmn-engine.service';
import {BP_Status, State, USER_SELECTION_STATUS} from '../state';
import {selectBpmnProcessId, selectExecutionPath, selectGetUserSelectionStatus} from '../selectors';
import {of} from 'rxjs/observable/of';
import {BpmnXmlService} from '@ispm/shared/bpmn/services/bpmn-xml.service';
import {SetErrorAction} from '@ispm/shared/store/actions/error.actions';
import {
  failedToGetBpmnExecutionStatus,
  failedToLoadBpmnXml,
  failedToStartBpmnExecution
} from '@ispm-client/modules/process-executor/models/constants';
import {selectKnowledgeBase} from "@ispm-client/modules/pathways/store/selectors/knowledge-base.selector";
import {forkJoin} from "rxjs";

@Injectable()
export class BpmnProcessEffects {
  private bpmnProcessId: string;

  constructor(private actions$: Actions,
              private store$: Store<State>,
              private store: Store<any>,
              private injector: Injector) {
    this.store$.pipe(select(
      selectBpmnProcessId
    )).subscribe(bpmnProcessId => this.bpmnProcessId = bpmnProcessId);

  }

  private getBpmnEngineService(): BpmnEngineService {
    return this.injector.get(BpmnEngineService);
  }

  private getBpmnXmlService(): BpmnXmlService {
    return this.injector.get(BpmnXmlService);
  }
  @Effect()
  initExecutionEffect$: Observable<Action> = this.actions$.pipe(
    ofType<InitExecutionAction>(
      BpmnProcessActionTypes.INIT_EXECUTION
    ),
    switchMap(action =>
      [
        new UpdateBpmnDeploymentIdentifierAction({bpmnDeploymentIdentifier: action.payload.bpmnDeplymentIdentifier}),
        new FetchBpmnXmlAction({bpmnDeploymentIdentifier: action.payload.bpmnDeplymentIdentifier}),
        new StartExecutionAction({
          bpmnProcessId: action.payload.bpmnDeplymentIdentifier.processId,
          patientData: action.payload.patientData
        })
      ]
    )
  );

  @Effect()
  loadExistingExecutionEffect$: Observable<Action> = this.actions$.pipe(
    ofType<LoadExsitingExecutionAction>(
      BpmnProcessActionTypes.LOAD_EXISTING_EXECUTION
    ),
    switchMap(action =>
      [
        new UpdateBpmnDeploymentIdentifierAction({bpmnDeploymentIdentifier: action.payload.bpmnDeploymentIdentifier}),
        new FetchBpmnXmlAction({bpmnDeploymentIdentifier: action.payload.bpmnDeploymentIdentifier}),
        new UpdateProcessInstanceAction({processInstanceId: action.payload.processInstanceId}),
        new FetchExecutionHistoryAction({processInstanceId: action.payload.processInstanceId})
      ]
    )
  );

  @Effect()
  fetchBpmnXmlEffect$: Observable<Action> = this.actions$.pipe(
    ofType<FetchBpmnXmlAction>(
      BpmnXmlActionTypes.FETCH_BPMN_XML
    ),
    switchMap<any, any>((action) => {
      return forkJoin(
        Observable.of(action),
        this.store.pipe(select(selectKnowledgeBase)).pipe(filter(kb => !!kb),take(1))
      );
    }),
    switchMap(([action, knowledgeBase]) =>
      this.getBpmnXmlService()
        .getBpmnXml(action.payload.bpmnDeploymentIdentifier.deploymentId, action.payload.bpmnDeploymentIdentifier.name, knowledgeBase)
        .pipe(
          map(bpmnProcessXml => {
            if(bpmnProcessXml){
                return new UpdateBpmnXmlAction({bpmnXml: bpmnProcessXml});
            }else{
                return new SetErrorAction({
                    error: '',
                    devMessage: 'fetchBpmnXmlEffect$: No bpmn process found for name:  ' + action.payload.bpmnDeploymentIdentifier.name,
                    userMessage: failedToLoadBpmnXml
                })
            }}),
          catchError(error => of(new SetErrorAction({
            error,
            devMessage: 'fetchBpmnXmlEffect$: Failed to get BPMN XML',
            userMessage: failedToLoadBpmnXml
          })))
        )
    )
  );

  @Effect()
  startExecutionRequestEffect$: Observable<Action> = this.actions$.pipe(
    ofType<StartExecutionAction>(
      BpmnProcessActionTypes.START_EXECUTION
    ),
    switchMap<any, any>((action) => {
      return forkJoin(
        Observable.of(action),
        this.store.pipe(select(selectKnowledgeBase)).pipe(filter(kb => !!kb),take(1))
      );
    }),
    switchMap(([action, knowledgeBase]) =>
      this.getBpmnEngineService()
        .startExecution(action.payload.bpmnProcessId, action.payload.patientData, knowledgeBase)
        .pipe(
          switchMap(status =>
            [
              new UpdateProcessInstanceAction({processInstanceId: status.processInstanceId}),
              new UpdateExecutionHistoryParsingStatusAction({status: BP_Status.NotStarted}),
              new UpdateProcessExecutionStatus({processExecutionStatus: status})
            ]
          ),
          catchError(error => {
            this.store$.dispatch(new UpdateUserSelectionStatusAction({status: USER_SELECTION_STATUS.NotInProgress}));
            return of(new SetErrorAction({
              error,
              devMessage: 'startExecutionRequestEffect$: Failed to start BPMN execution',
              userMessage: failedToStartBpmnExecution
            }));
          })
        )
    )
  );

  @Effect()
  fetchExecutionHistoryEffect$: Observable<Action> = this.actions$.pipe(
    ofType<FetchExecutionHistoryAction>(
      BpmnProcessActionTypes.FETCH_EXECUTION_HISTORY
    ),
    switchMap<any, any>((action) => {
      return forkJoin(
        Observable.of(action),
        this.store.pipe(select(selectKnowledgeBase)).pipe(filter(kb => !!kb),take(1))
      );
    }),
    switchMap(([action, knowledgeBase]) =>
      this.getBpmnEngineService()
        .getExecutionStatus(action.payload.processInstanceId, knowledgeBase)
        .pipe(
          switchMap((executionStatus) =>
            [
              new UpdateExecutionHistoryParsingStatusAction({status: BP_Status.NotStarted}),
              new UpdateProcessExecutionStatus({processExecutionStatus: executionStatus})
            ]),
          catchError(error => of(new SetErrorAction({
            error,
            devMessage: 'fetchExecutionHistoryEffect$: Failed to get BPMN execution status',
            userMessage: failedToGetBpmnExecutionStatus
          })))
        )
    )
  );

  @Effect()
  restartExecution: Observable<Action> = this.actions$.pipe(
    ofType<RestartExecutionAction>(
      BpmnProcessActionTypes.RESTART_EXECUTION
    ),
    withLatestFrom(this.store$.pipe(select(selectExecutionPath)), this.store$.pipe(select(selectGetUserSelectionStatus)).take(1)),
    switchMap( ([action, executionPath, userStatus]) =>
    {
        if (userStatus != USER_SELECTION_STATUS.InProgress) {
          return [
            new UpdateUserSelectionStatusAction({status: USER_SELECTION_STATUS.InProgress}),
            new UserClickedExecutedNodeAction({id: executionPath.executedNodes[0].nodeId})]
        } else {
          return [];
        }
      }
    )
  );
}
